import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useEmailUsage } from '../../hooks/useAccount';

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateStr));
}

export default function EmailUsagePage() {
  const { data: usage, isLoading, isError, error } = useEmailUsage();

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={48} />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 2 }} />
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rectangular" height={100} sx={{ mt: 2, borderRadius: 2 }} />
        ))}
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        {(error as Error)?.message ?? 'Failed to load email usage'}
      </Alert>
    );
  }

  if (!usage) return null;

  const overallPercent =
    usage.totalEmailsIncluded > 0
      ? Math.min(100, Math.round((usage.totalEmailsSent / usage.totalEmailsIncluded) * 100))
      : 0;

  const hasOverage = usage.totalOverageCount > 0;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <EmailIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>
          Email Usage
        </Typography>
      </Stack>

      {/* Overall Usage Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={1}>
            Overall Usage
          </Typography>

          <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1}>
            <Typography variant="body1">
              {usage.totalEmailsSent.toLocaleString()} / {usage.totalEmailsIncluded.toLocaleString()} emails used
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {overallPercent}%
            </Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={overallPercent}
            sx={{
              height: 10,
              borderRadius: 5,
              mb: 2,
              '& .MuiLinearProgress-bar': {
                bgcolor: overallPercent >= 90 ? 'error.main' : overallPercent >= 75 ? 'warning.main' : 'primary.main',
              },
            }}
          />

          {hasOverage && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <WarningAmberIcon color="warning" fontSize="small" />
              <Typography variant="body2" color="warning.main">
                {usage.totalOverageCount.toLocaleString()} overage email{usage.totalOverageCount !== 1 ? 's' : ''} ({formatCents(usage.totalOverageCostCents)})
              </Typography>
            </Stack>
          )}

          <Typography variant="body2" color="text.secondary" mt={1}>
            Each guest includes 5 emails (invites, reminders, updates). Overages are billed at $0.15/email.
          </Typography>
        </CardContent>
      </Card>

      {/* Per-Event Breakdown */}
      <Typography variant="h6" fontWeight={600} mb={2}>
        Per-Event Breakdown
      </Typography>

      {usage.events.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" textAlign="center" py={3}>
              No events yet. Create an event to start tracking email usage.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {usage.events.map((event) => {
            const percent =
              event.emailsIncluded > 0
                ? Math.min(100, Math.round((event.emailsSent / event.emailsIncluded) * 100))
                : 0;
            const approaching = percent >= 75;
            const atLimit = percent >= 100;

            return (
              <Card key={event.eventId}>
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ sm: 'center' }}
                    mb={1}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {event.eventTitle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(event.eventDate)} &middot; {event.guestCount} guest{event.guestCount !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    {atLimit && (
                      <Chip label="Over Limit" color="error" size="small" />
                    )}
                    {approaching && !atLimit && (
                      <Chip label="Approaching Limit" color="warning" size="small" />
                    )}
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={0.5}>
                    <Typography variant="body2">
                      {event.emailsSent} / {event.emailsIncluded} emails used
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {percent}%
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={percent}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      '& .MuiLinearProgress-bar': {
                        bgcolor: atLimit ? 'error.main' : approaching ? 'warning.main' : 'primary.main',
                      },
                    }}
                  />

                  {event.overageCount > 0 && (
                    <Typography variant="body2" color="error.main" mt={1}>
                      {event.overageCount} overage email{event.overageCount !== 1 ? 's' : ''} &middot; {formatCents(event.overageCostCents)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
